/* eslint-disable prettier/prettier */
import { is, getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import asyncCapableHelper from "bpmn-js-properties-panel/lib/helper/AsyncCapableHelper";
import eventDefinitionHelper from "bpmn-js-properties-panel/lib/helper/EventDefinitionHelper";
import PropertiesActivator from "bpmn-js-properties-panel/lib/PropertiesActivator";
import executableProps from "bpmn-js-properties-panel/lib/provider/bpmn/parts/ExecutableProps";
import idProps from "bpmn-js-properties-panel/lib/provider/bpmn/parts/IdProps";
import linkProps from "bpmn-js-properties-panel/lib/provider/bpmn/parts/LinkProps";
import nameProps from "bpmn-js-properties-panel/lib/provider/bpmn/parts/NameProps";
import processProps from "bpmn-js-properties-panel/lib/provider/bpmn/parts/ProcessProps";
import { getTemplateId } from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/Helper";
import elementTemplateChooserProps from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/parts/ChooserProps";
import elementTemplateCustomProps from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/parts/CustomProps";
import elementTemplateDescriptionProps from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/parts/DescriptionProps";
import elementTemplateInputParametersProps from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/parts/InputParametersProps";
import elementTemplateOutputParametersProps from "bpmn-js-properties-panel/lib/provider/camunda/element-templates/parts/OutputParametersProps";
import asynchronousContinuationProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/AsynchronousContinuationProps";
import conditionalProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/ConditionalProps";
import connectorDetails from "bpmn-js-properties-panel/lib/provider/camunda/parts/ConnectorDetailProps";
import connectorInputParameters from "bpmn-js-properties-panel/lib/provider/camunda/parts/ConnectorInputParametersProps";
import connectorOutputParameters from "bpmn-js-properties-panel/lib/provider/camunda/parts/ConnectorOutputParametersProps";
import errorProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/ErrorEventProps";
import externalTaskConfiguration from "bpmn-js-properties-panel/lib/provider/camunda/parts/ExternalTaskConfigurationProps";
import fieldInjections from "bpmn-js-properties-panel/lib/provider/camunda/parts/FieldInjectionProps";
import historyTimeToLive from "bpmn-js-properties-panel/lib/provider/camunda/parts/HistoryTimeToLiveProps";
import inputParameters from "bpmn-js-properties-panel/lib/provider/camunda/parts/InputParametersProps";
import jobConfiguration from "bpmn-js-properties-panel/lib/provider/camunda/parts/JobConfigurationProps";
import listenerDetails from "bpmn-js-properties-panel/lib/provider/camunda/parts/ListenerDetailProps";
import listenerFields from "bpmn-js-properties-panel/lib/provider/camunda/parts/ListenerFieldInjectionProps";
import listenerProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/ListenerProps";
import multiInstanceProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/MultiInstanceLoopProps";
import outputParameters from "bpmn-js-properties-panel/lib/provider/camunda/parts/OutputParametersProps";
import processVariablesProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/ProcessVariablesProps";
import properties from "bpmn-js-properties-panel/lib/provider/camunda/parts/PropertiesProps";
import scriptProps from "bpmn-js-properties-panel/lib/provider/camunda/parts/ScriptTaskProps";
import startEventInitiator from "bpmn-js-properties-panel/lib/provider/camunda/parts/StartEventInitiator";
import tasklist from "bpmn-js-properties-panel/lib/provider/camunda/parts/TasklistProps";
import variableMapping from "bpmn-js-properties-panel/lib/provider/camunda/parts/VariableMappingProps";
import versionTag from "bpmn-js-properties-panel/lib/provider/camunda/parts/VersionTagProps";
import inherits from "inherits";

import callActivityProps from "./parts/CallActivityProps";
import candidateStarter from "./parts/CandidateStarterProps";
import documentationProps from "./parts/DocumentationProps";
import eventProps from "./parts/EventProps";
import executavelAutomaticProcessProps from "./parts/ExecutavelAutomaticProcessProps";
import executavelPortalExternoProps from "./parts/ExecutavelPortalExternoProps";
import ImplementationTypeHelper from "./parts/ImplementationTypeHelper";
import serviceTaskDelegateProps from "./parts/ServiceTaskDelegateProps";
import smartProperties from "./parts/smartProperties";
import smartSignatureProperties from "./parts/smartSignatureProperties";
import userTaskProps from "./parts/UserTaskProps";

// helpers ////////////////////////////////////////

function isExternalTaskPriorityEnabled(element) {
  const businessObject = getBusinessObject(element);

  // show only if element is a process, a participant ...
  // eslint-disable-next-line
  if (
    is(element, "bpmn:Process") ||
    (is(element, "bpmn:Participant") && businessObject.get("processRef"))
  ) {
    return true;
  }

  const externalBo =
      ImplementationTypeHelper.getServiceTaskLikeBusinessObject(element),
    isExternalTask =
      ImplementationTypeHelper.getImplementationType(externalBo) === "external";

  // ... or an external task with selected external implementation type
  return (
    !!ImplementationTypeHelper.isExternalCapable(externalBo) && isExternalTask
  );
}

function isJobConfigEnabled(element) {
  const businessObject = getBusinessObject(element);
  // eslint-disable-next-line
  if (
    is(element, "bpmn:Process") ||
    (is(element, "bpmn:Participant") && businessObject.get("processRef"))
  ) {
    return true;
  }

  // async behavior
  const bo = getBusinessObject(element);
  if (
    asyncCapableHelper.isAsyncBefore(bo) ||
    asyncCapableHelper.isAsyncAfter(bo)
  ) {
    return true;
  }

  // timer definition
  if (is(element, "bpmn:Event")) {
    return !!eventDefinitionHelper.getTimerEventDefinition(element);
  }

  return false;
}

function getListenerLabel(param, translate) {
  if (is(param, "camunda:ExecutionListener")) {
    return translate("Execution Listener");
  }

  if (is(param, "camunda:TaskListener")) {
    return translate("Task Listener");
  }

  return "";
}

const PROCESS_KEY_HINT = "This maps to the process definition key.";
const TASK_KEY_HINT = "This maps to the task definition key.";

function getIdOptions(element) {
  if (is(element, "bpmn:Process")) {
    return { description: PROCESS_KEY_HINT };
  }

  if (is(element, "bpmn:UserTask")) {
    return { description: TASK_KEY_HINT };
  }
}

function getProcessOptions(element) {
  if (is(element, "bpmn:Participant")) {
    return { processIdDescription: PROCESS_KEY_HINT };
  }
}

function createElementTemplateGroups(
  element,
  bpmnFactory,
  canvas,
  elementTemplates,
  modeling,
  replace,
  selection,
  translate,
) {
  const templateId = getTemplateId(element);

  if (!templateId) {
    return [];
  }

  const descriptionGroup = elementTemplateDescriptionProps(
    element,
    elementTemplates,
    modeling,
    replace,
    selection,
    translate,
  );

  const idOptions = getIdOptions(element) || {};

  idOptions.id = "element-template-element-id";

  const nameOptions = { id: "element-template-element-name" };

  idProps(descriptionGroup, element, translate, idOptions);
  nameProps(
    descriptionGroup,
    element,
    bpmnFactory,
    canvas,
    translate,
    nameOptions,
  );
  processProps(
    descriptionGroup,
    element,
    translate,
    getProcessOptions(element),
  );

  const elementTemplateInputParametersGroup = {
    id: "template-inputs",
    label: translate("Input Parameters"),
    entries: [],
  };
  elementTemplateInputParametersProps(
    elementTemplateInputParametersGroup,
    element,
    elementTemplates,
    bpmnFactory,
    translate,
  );

  const elementTemplateOutputParametersGroup = {
    id: "template-outputs",
    label: translate("Output Parameters"),
    entries: [],
  };
  elementTemplateOutputParametersProps(
    elementTemplateOutputParametersGroup,
    element,
    elementTemplates,
    bpmnFactory,
    translate,
  );

  const customFieldsGroups = elementTemplateCustomProps(
    element,
    elementTemplates,
    bpmnFactory,
    translate,
  );

  return [
    descriptionGroup,
    elementTemplateInputParametersGroup,
    elementTemplateOutputParametersGroup,
  ].concat(customFieldsGroups);
}

function createGeneralTabGroups(
  element,
  canvas,
  bpmnFactory,
  elementRegistry,
  elementTemplates,
  translate,
  modeling,
) {
  // refer to target element for external labels
  element = element.labelTarget || element;

  const generalGroup = {
    id: "general",
    label: translate("General"),
    entries: [],
  };

  idProps(generalGroup, element, translate, getIdOptions(element));
  nameProps(generalGroup, element, bpmnFactory, canvas, translate);
  processProps(generalGroup, element, translate, getProcessOptions(element));
  versionTag(generalGroup, element, translate);
  executableProps(generalGroup, element, translate);
  executavelPortalExternoProps(
    generalGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );
  executavelAutomaticProcessProps(
    generalGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );
  elementTemplateChooserProps(
    generalGroup,
    element,
    elementTemplates,
    translate,
  );

  const detailsGroup = {
    id: "details",
    label: translate("Details"),
    entries: [],
  };
  serviceTaskDelegateProps(
    detailsGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );
  userTaskProps(detailsGroup, element, translate, modeling);
  scriptProps(detailsGroup, element, bpmnFactory, translate);
  linkProps(detailsGroup, element, translate);
  callActivityProps(detailsGroup, element, bpmnFactory, translate, modeling);
  eventProps(
    detailsGroup,
    element,
    bpmnFactory,
    elementRegistry,
    translate,
    modeling,
  );
  errorProps(detailsGroup, element, bpmnFactory, translate);
  conditionalProps(detailsGroup, element, bpmnFactory, translate);
  startEventInitiator(detailsGroup, element, translate); // this must be the last element of the details group!

  const multiInstanceGroup = {
    id: "multiInstance",
    label: translate("Multi Instance"),
    entries: [],
  };
  multiInstanceProps(multiInstanceGroup, element, bpmnFactory, translate);

  const asyncGroup = {
    id: "async",
    label: translate("Asynchronous Continuations"),
    entries: [],
  };
  asynchronousContinuationProps(asyncGroup, element, bpmnFactory, translate);

  const jobConfigurationGroup = {
    id: "jobConfiguration",
    label: translate("Job Configuration"),
    entries: [],
    enabled: isJobConfigEnabled,
  };
  jobConfiguration(jobConfigurationGroup, element, bpmnFactory, translate);

  const externalTaskGroup = {
    id: "externalTaskConfiguration",
    label: translate("External Task Configuration"),
    entries: [],
    enabled: isExternalTaskPriorityEnabled,
  };
  externalTaskConfiguration(externalTaskGroup, element, bpmnFactory, translate);

  const candidateStarterGroup = {
    id: "candidateStarterConfiguration",
    label: translate("Candidate Starter Configuration"),
    entries: [],
  };
  candidateStarter(
    candidateStarterGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );

  const historyTimeToLiveGroup = {
    id: "historyConfiguration",
    label: translate("History Configuration"),
    entries: [],
  };
  historyTimeToLive(historyTimeToLiveGroup, element, bpmnFactory, translate);

  const tasklistGroup = {
    id: "tasklist",
    label: translate("Tasklist Configuration"),
    entries: [],
  };
  tasklist(tasklistGroup, element, bpmnFactory, translate);

  const documentationGroup = {
    id: "documentation",
    label: "",
    entries: [],
  };
  documentationProps(
    documentationGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );

  const groups = [];
  groups.push(generalGroup);
  groups.push(detailsGroup);
  groups.push(externalTaskGroup);
  groups.push(multiInstanceGroup);
  groups.push(asyncGroup);
  groups.push(jobConfigurationGroup);
  groups.push(candidateStarterGroup);
  groups.push(historyTimeToLiveGroup);
  groups.push(tasklistGroup);
  groups.push(documentationGroup);

  return groups;
}
function createVariablesTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
) {
  const variablesGroup = {
    id: "variables",
    label: translate("Variables"),
    entries: [],
  };
  variableMapping(variablesGroup, element, bpmnFactory, translate);

  return [variablesGroup];
}

function createProcessVariablesTabGroups(element, translate) {
  const processVariablesGroup = {
    id: "process-variables",
    label: translate("Variables"),
    entries: [],
  };

  processVariablesProps(processVariablesGroup, element, translate);

  return [processVariablesGroup];
}

function createFormsTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
  modeling,
) {
  const formGroup = {
    id: "forms",
    label: translate("Forms"),
    entries: [],
  };
  smartProperties(formGroup, element, bpmnFactory, translate, modeling);

  return [formGroup];
}

function createAssinaturasTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
  modeling,
) {
  const signaturesGroup = {
    id: "signatures",
    label: translate("Signature"),
    entries: [],
  };
  smartSignatureProperties(
    signaturesGroup,
    element,
    bpmnFactory,
    translate,
    modeling,
  );

  return [signaturesGroup];
}

function createListenersTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
) {
  const listenersGroup = {
    id: "listeners",
    label: translate("Listeners"),
    entries: [],
  };

  const options = listenerProps(
    listenersGroup,
    element,
    bpmnFactory,
    translate,
  );

  const listenerDetailsGroup = {
    id: "listener-details",
    entries: [],
    enabled(element, node) {
      return options.getSelectedListener(element, node);
    },
    label(element, node) {
      const param = options.getSelectedListener(element, node);
      return getListenerLabel(param, translate);
    },
  };

  listenerDetails(
    listenerDetailsGroup,
    element,
    bpmnFactory,
    options,
    translate,
  );

  const listenerFieldsGroup = {
    id: "listener-fields",
    label: translate("Field Injection"),
    entries: [],
    enabled(element, node) {
      return options.getSelectedListener(element, node);
    },
  };

  listenerFields(listenerFieldsGroup, element, bpmnFactory, options, translate);

  return [listenersGroup, listenerDetailsGroup, listenerFieldsGroup];
}

function createInputOutputTabGroups(
  element,
  bpmnFactory,
  elementTemplates,
  translate,
) {
  const inputParametersGroup = {
    id: "input-parameters",
    label: translate("Input Parameters"),
    entries: [],
  };

  inputParameters(
    inputParametersGroup,
    element,
    bpmnFactory,
    elementTemplates,
    translate,
  );

  const outputParametersGroup = {
    id: "output-parameters",
    label: translate("Output Parameters"),
    entries: [],
  };

  outputParameters(
    outputParametersGroup,
    element,
    bpmnFactory,
    elementTemplates,
    translate,
  );

  return [inputParametersGroup, outputParametersGroup];
}

function createConnectorTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
) {
  const connectorDetailsGroup = {
    id: "connector-details",
    label: translate("Details"),
    entries: [],
  };

  connectorDetails(connectorDetailsGroup, element, bpmnFactory, translate);

  const connectorInputParametersGroup = {
    id: "connector-input-parameters",
    label: translate("Input Parameters"),
    entries: [],
  };

  connectorInputParameters(
    connectorInputParametersGroup,
    element,
    bpmnFactory,
    translate,
  );

  const connectorOutputParametersGroup = {
    id: "connector-output-parameters",
    label: translate("Output Parameters"),
    entries: [],
  };

  connectorOutputParameters(
    connectorOutputParametersGroup,
    element,
    bpmnFactory,
    translate,
  );

  return [
    connectorDetailsGroup,
    connectorInputParametersGroup,
    connectorOutputParametersGroup,
  ];
}

function createFieldInjectionsTabGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
) {
  const fieldGroup = {
    id: "field-injections-properties",
    label: translate("Field Injections"),
    entries: [],
  };

  fieldInjections(fieldGroup, element, bpmnFactory, translate);

  return [fieldGroup];
}

function createExtensionElementsGroups(
  element,
  bpmnFactory,
  elementRegistry,
  translate,
) {
  const propertiesGroup = {
    id: "extensionElements-properties",
    label: translate("Properties"),
    entries: [],
  };
  properties(propertiesGroup, element, bpmnFactory, translate);

  return [propertiesGroup];
}

// Camunda Properties Provider /////////////////////////////////////

/**
 * A properties provider for Camunda related properties.
 *
 * @param {BpmnFactory} bpmnFactory
 * @param {Canvas} canvas
 * @param {ElementRegistry} elementRegistry
 * @param {ElementTemplates} elementTemplates
 * @param {EventBus} eventBus
 * @param {Modeling} modeling
 * @param {Replace} replace
 * @param {Selection} selection
 * @param {Translate} translate
 */
export default function SmartPropertiesProvider(
  bpmnFactory,
  canvas,
  elementRegistry,
  elementTemplates,
  eventBus,
  modeling,
  replace,
  selection,
  translate,
) {
  PropertiesActivator.call(this, eventBus);

  this.getTabs = function (element) {
    const generalTab = {
      id: "general",
      label: translate("General"),
      groups: createGeneralTabGroups(
        element,
        canvas,
        bpmnFactory,
        elementRegistry,
        elementTemplates,
        translate,
        modeling,
      ),
    };

    const elementTemplateTab = {
      id: "element-template",
      label: translate("Template"),
      groups: createElementTemplateGroups(
        element,
        bpmnFactory,
        canvas,
        elementTemplates,
        modeling,
        replace,
        selection,
        translate,
      ),
    };

    const variablesTab = {
      id: "variables",
      label: translate("Variables"),
      groups: createVariablesTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
      ),
    };

    const processVariablesTab = {
      id: "process-variables",
      label: translate("Variables"),
      groups: createProcessVariablesTabGroups(element, translate),
    };

    const formsTab = {
      id: "forms",
      label: translate("Forms"),
      groups: createFormsTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
        modeling,
      ),
    };

    const assinaturasTab = {
      id: "signatures",
      label: translate("Signature"),
      groups: createAssinaturasTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
        modeling,
      ),
    };

    const listenersTab = {
      id: "listeners",
      label: translate("Listeners"),
      groups: createListenersTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
      ),
      enabled(element) {
        return (
          !eventDefinitionHelper.getLinkEventDefinition(element) ||
          (!is(element, "bpmn:IntermediateThrowEvent") &&
            eventDefinitionHelper.getLinkEventDefinition(element))
        );
      },
    };

    const inputOutputTab = {
      id: "input-output",
      label: translate("Input/Output"),
      groups: createInputOutputTabGroups(
        element,
        bpmnFactory,
        elementTemplates,
        translate,
      ),
    };

    const connectorTab = {
      id: "connector",
      label: translate("Connector"),
      groups: createConnectorTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
      ),
      enabled(element) {
        const bo =
          ImplementationTypeHelper.getServiceTaskLikeBusinessObject(element);
        return (
          bo &&
          ImplementationTypeHelper.getImplementationType(bo) === "connector"
        );
      },
    };

    const fieldInjectionsTab = {
      id: "field-injections",
      label: translate("Field Injections"),
      groups: createFieldInjectionsTabGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
      ),
    };

    const extensionsTab = {
      id: "extensionElements",
      label: translate("Extensions"),
      groups: createExtensionElementsGroups(
        element,
        bpmnFactory,
        elementRegistry,
        translate,
      ),
    };

    return [
      generalTab,
      elementTemplateTab,
      variablesTab,
      processVariablesTab,
      connectorTab,
      formsTab,
      assinaturasTab,
      listenersTab,
      inputOutputTab,
      fieldInjectionsTab,
      extensionsTab,
    ];
  };
}

SmartPropertiesProvider.$inject = [
  "bpmnFactory",
  "canvas",
  "elementRegistry",
  "elementTemplates",
  "eventBus",
  "modeling",
  "replace",
  "selection",
  "translate",
];

inherits(SmartPropertiesProvider, PropertiesActivator);
